/**
 * Microsoft Entra (Azure AD) sign-in via MSAL.js, redirect flow.
 *
 * Ported from `../agent-house/src/auth/msal.ts`, which is the reference
 * implementation for Entra in this estate, and simplified: agent-house keys one
 * PublicClientApplication per (tenant, client) because its auth is per-agent
 * off `/api/agents`. This SPA talks to exactly one API, so the config comes
 * from the build-time VITE_ENTRA_* vars and there is one instance.
 *
 * What is kept verbatim from agent-house, because each line was paid for:
 *
 *   * **Redirect, not popup.** Popup polling is fragile in dev, blocked by some
 *     browser settings, and the popup is a second top-level window that needs
 *     its own MSAL config.
 *   * **`redirectUri: window.location.origin`.** Azure matches redirect URIs
 *     EXACTLY, so an origin and the same origin with a trailing slash are two
 *     different registrations. Deriving it from `location.origin` rather than
 *     an env var means the value can only ever be the one the browser is
 *     actually on — one fewer way to ship a UI that dies on
 *     AADSTS50011 (redirect URI mismatch).
 *   * **`sessionStorage`, not the localStorage default.** A token in
 *     localStorage outlives the tab and is readable by any script on the
 *     origin.
 *   * **`initialize()` must complete** before any request API is called on a
 *     fresh instance, and **`handleRedirectPromise()` must run on mount** or the
 *     response hash from login.microsoftonline.com is never parsed and the user
 *     lands back on the sign-in gate having just signed in successfully.
 *
 * If VITE_ENTRA_TENANT_ID / VITE_ENTRA_CLIENT_ID are empty this module degrades
 * to "no auth", which is the local-dev arrangement (the API defaults
 * LEADERBOARD_AUTH_REQUIRED off). It is NOT the deployed one — the Terraform
 * task definition sets auth on.
 */
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser'

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID ?? ''
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID ?? ''

/**
 * The API scope MSAL requests, e.g. `api://<client-id>/user`.
 *
 * This must be an API scope on the app registration, not `openid`/`profile`:
 * those yield an ID token and an access token whose audience is Microsoft
 * Graph, and api/auth.py would reject the latter's audience. Falls back to the
 * conventional `api://<cid>/user` MoAgent uses, so a missing var is a working
 * default rather than a silent scope-less request.
 */
const SCOPE =
  import.meta.env.VITE_ENTRA_API_SCOPE ??
  (CLIENT_ID ? `api://${CLIENT_ID}/user` : '')

/** False in local dev; true wherever Terraform supplied the app registration. */
export const authEnabled = Boolean(TENANT_ID && CLIENT_ID && SCOPE)

const SCOPES = SCOPE ? [SCOPE] : []

function buildConfig(): Configuration {
  return {
    auth: {
      clientId: CLIENT_ID,
      authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: 'sessionStorage' },
  }
}

let _pca: PublicClientApplication | null = null
let _init: Promise<void> | null = null

async function instance(): Promise<PublicClientApplication | null> {
  if (!authEnabled) return null
  if (!_pca) _pca = new PublicClientApplication(buildConfig())
  // initialize() is idempotent, but it must have RESOLVED before any request
  // API is called, so the promise is memoised rather than the boolean.
  if (!_init) _init = _pca.initialize()
  await _init
  return _pca
}

function activeAccount(pca: PublicClientApplication): AccountInfo | null {
  const active = pca.getActiveAccount()
  if (active) return active
  const all = pca.getAllAccounts()
  if (all.length === 0) return null
  pca.setActiveAccount(all[0])
  return all[0]
}

let _redirectHandled = false

/**
 * Process the Entra redirect response, then report the signed-in account.
 *
 * Must run on mount, before anything else touches MSAL. Idempotent: React
 * StrictMode mounts effects twice in dev and `handleRedirectPromise` consumes a
 * one-time response.
 */
export async function bootstrap(): Promise<AccountInfo | null> {
  const pca = await instance()
  if (!pca) return null
  if (_redirectHandled) return activeAccount(pca)
  _redirectHandled = true

  const result = await pca.handleRedirectPromise()
  if (result?.account) {
    pca.setActiveAccount(result.account)
    return result.account
  }
  return activeAccount(pca)
}

/**
 * Navigate the whole tab to Entra. Control does not return — the returned
 * promise is a placeholder for the call site that started the redirect.
 */
export async function login(): Promise<void> {
  const pca = await instance()
  if (!pca) return
  try {
    await pca.loginRedirect({ scopes: SCOPES })
  } catch (err) {
    if (
      err instanceof BrowserAuthError &&
      err.errorCode === 'interaction_in_progress'
    ) {
      // A redirect that was aborted (back button, closed tab mid-flow) leaves
      // the flag set in sessionStorage and every later sign-in attempt throws
      // instead of navigating. Clear it and retry once.
      try {
        ;(
          pca as unknown as {
            browserStorage?: { setInteractionInProgress?: (b: boolean) => void }
          }
        ).browserStorage?.setInteractionInProgress?.(false)
      } catch {
        /* fall through to the user-visible error */
      }
      await pca.loginRedirect({ scopes: SCOPES })
    } else {
      throw err
    }
  }
}

/**
 * Why the last `acquireToken()` returned null, as an MSAL error code.
 *
 * Read by the sign-in gate to tell "signed out" apart from "signed in, but the
 * tenant has not consented to this API scope" — two states that are otherwise
 * pixel-identical and have opposite remedies. Module-level rather than React
 * state because `acquireToken` is called from the API client, outside any
 * component; the gate only ever reads it during a render some *other* state
 * change already triggered.
 */
let _lastTokenErrorCode: string | null = null

export function lastTokenErrorCode(): string | null {
  return _lastTokenErrorCode
}

/**
 * An access token for the API, or null.
 *
 * Silent only — it never redirects. The API client calls this on every request,
 * and a redirect from inside a fetch would throw the user out of the page
 * mid-render with no explanation; a null here becomes a 401 and the sign-in
 * gate, which says what happened.
 */
export async function acquireToken(): Promise<string | null> {
  const pca = await instance()
  if (!pca) return null
  const account = activeAccount(pca)
  if (!account) return null
  try {
    const result = await pca.acquireTokenSilent({ scopes: SCOPES, account })
    _lastTokenErrorCode = null
    return result.accessToken
  } catch (err) {
    // InteractionRequiredAuthError is the expected "session is gone" case;
    // anything else (transient network, poisoned cache) is equally unusable
    // here. Both mean "no token", and the caller shows the gate.
    //
    // `consent_required` / `interaction_required` arrive as this same subclass
    // when the tenant requires admin consent for the API scope and no admin has
    // granted it. Signing in again cannot fix that, so the code is recorded for
    // the gate rather than collapsed into a bare null.
    _lastTokenErrorCode =
      err instanceof InteractionRequiredAuthError ||
      err instanceof BrowserAuthError
        ? err.errorCode
        : 'unknown_error'
    if (!(err instanceof InteractionRequiredAuthError)) {
      console.warn('silent token acquisition failed', err)
    }
    return null
  }
}

/**
 * Drop the local session.
 *
 * `clearCache` rather than `logoutRedirect`: this app's session lives in
 * sessionStorage for one tab, and a full RP-initiated logout would also end the
 * user's Entra SSO session for every other Corteva app in the browser.
 */
export async function logout(): Promise<void> {
  const pca = await instance()
  if (!pca) return
  const account = activeAccount(pca)
  if (account) {
    try {
      await pca.clearCache({ account })
    } catch {
      /* best effort */
    }
  }
  pca.setActiveAccount(null)
}
